import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Star, MapPin, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

interface SmartSignupBannerProps {
  airportCode: string;
  airportName: string;
}

const STORAGE_KEY = "awb_visit_counts";
const DISMISSED_KEY = "awb_nudge_dismissed";

const getVisitCounts = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const incrementVisit = (code: string) => {
  const counts = getVisitCounts();
  counts[code] = (counts[code] || 0) + 1;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
  return counts;
};

const isDismissed = (): boolean => {
  try {
    const ts = localStorage.getItem(DISMISSED_KEY);
    if (!ts) return false;
    // Re-show after 7 days
    return Date.now() - Number(ts) < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const SmartSignupBanner = ({ airportCode, airportName }: SmartSignupBannerProps) => {
  const { user } = useAuth();
  const [message, setMessage] = useState<{ text: string; icon: React.ReactNode } | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (user || isDismissed()) return;

    const counts = incrementVisit(airportCode);
    const totalVisits = Object.values(counts).reduce((a, b) => a + b, 0);
    const thisAirportVisits = counts[airportCode] || 0;

    // Contextual messages
    if (thisAirportVisits >= 2) {
      setMessage({
        text: `Welcome back to ${airportCode}! Save it as your home airport for instant access.`,
        icon: <MapPin className="h-4 w-4" />,
      });
    } else if (totalVisits >= 3) {
      setMessage({
        text: "Check airports often? Save your favorites — create a free account.",
        icon: <Star className="h-4 w-4" />,
      });
    } else {
      return; // Don't show on first visit
    }

    // Show after a short delay
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [airportCode, user]);

  if (!visible || !message) return null;

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50 animate-slide-in-bottom">
      <div className="bg-card border border-border shadow-lg rounded-xl p-4 flex items-start gap-3">
        <span className="text-primary mt-0.5 flex-shrink-0">{message.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground">{message.text}</p>
          <Button asChild size="sm" className="mt-2 min-h-[36px]">
            <Link to="/auth">Create free account</Link>
          </Button>
        </div>
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
